// -----------------------------------------------------------------------------
// useAuth — Custom hook pour la gestion de l'authentification & profil
// StockPro / VisualisPro
//
// Gère :
//  - L'écoute de l'état Firebase Auth
//  - Le chargement temps réel du profil utilisateur depuis Firestore
//  - La déconnexion
//  - La réinitialisation de mot de passe
//  - Le calcul de l'état de licence (active / expirée)
//  - L'identifiant restaurant (multi-tenant)
// -----------------------------------------------------------------------------

import { useState, useEffect, useCallback } from "react";
import {
  onAuthStateChanged,
  signOut,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";

import { auth, db, USERS_COL } from "../firebase";
import { DEFAULT_ACCENT } from "../styles/theme";

export function useAuth() {
  // undefined = en cours de chargement initial
  // null      = déconnecté
  // object    = utilisateur Firebase connecté
  const [authUser, setAuthUser] = useState(undefined);
  const [userProfile, setUserProfile] = useState(null);

  // -- Écoute Firebase Auth + profil Firestore --------------------------------
  useEffect(() => {
    let unsubProfile = null;

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      // Nettoie l'écouteur de profil précédent si on change d'utilisateur
      if (unsubProfile) {
        unsubProfile();
        unsubProfile = null;
      }

      setAuthUser(user);

      if (user) {
        // Écoute temps réel du profil depuis Firestore
        const profileDoc = doc(db, USERS_COL, user.uid);
        unsubProfile = onSnapshot(
          profileDoc,
          (snap) => {
            if (snap.exists()) {
              setUserProfile(snap.data());
            } else {
              // Profil par défaut si pas encore créé en base
              setUserProfile({
                name: user.email,
                color: DEFAULT_ACCENT,
                type: "Restaurant",
              });
            }
          },
          (err) => {
            console.error("Erreur chargement profil:", err);
            setUserProfile({
              name: user.email,
              color: DEFAULT_ACCENT,
              type: "Restaurant",
            });
          }
        );
      } else {
        setUserProfile(null);
      }
    });

    return () => {
      if (unsubProfile) unsubProfile();
      unsubAuth();
    };
  }, []);

  // -- Déconnexion ------------------------------------------------------------
  const logout = useCallback(async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error("Erreur déconnexion:", err);
      throw err;
    }
  }, []);

  // -- Réinitialisation mot de passe ------------------------------------------
  const resetPassword = useCallback(async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      console.error("Erreur reset password:", err);
      throw err;
    }
  }, []);

  // -- États dérivés ----------------------------------------------------------
  const loading = authUser === undefined;
  const isLoggedIn = !!authUser;

  // Identifiant restaurant : si l'utilisateur appartient à un restaurant
  // partagé (mode multi-staff), on prend restaurantId, sinon son propre uid.
  const restaurantId = userProfile?.restaurantId || authUser?.uid || null;

  // Statut de licence : expirée si non active OU date de fin dépassée
  const licenceExpired =
    !!userProfile &&
    (!userProfile.licenceActive ||
      (userProfile.licenceEnd &&
        new Date(userProfile.licenceEnd) < new Date()));

  return {
    authUser,
    userProfile,
    loading,
    isLoggedIn,
    licenceExpired,
    restaurantId,
    logout,
    resetPassword,
  };
}
